import type { AppDispatch, RootState } from 'app/store';
import { ahuBoardApi } from 'app/services/ahuBoardApi';
import { ahuBoardFormsApi, type IItemValidation } from 'app/services/ahuBoardFormsApi';
import {
  setAllErrors,
  replaceTunnel,
  mergeTunnelComponents,
  mergeComponentValidation,
  appendAccessoryValidation,
  mergeAccessoryOnChange,
  replaceSectionAccessories,
  mergeDmountValidation,
  removeDmountErrors,
  clearSectionOnDestroy,
  removeTunnelErrors,
  updateSectionTag,
  incValidating,
  decValidating,
} from 'app/slices/ahuValidationSlice';
import type { ITunnelValidation } from 'models';

/**
 * Phase 6e1 — unit.js validation orchestration (§22.3) ported as thunks.
 * Every partial validator ends in a slice merge with the legacy splice/replace
 * semantics; the error-manager + tint producer subscribe to the slice.
 *
 * `validatingCount` reproduces the activateValidating/deActivateValidating
 * lock fan-out — consumers derive `.error-log-validation.active`,
 * `.li_error_log.non-clickable` etc. from `selectValidating`.
 */

type Thunk<R = void> = (dispatch: AppDispatch, getState: () => RootState) => Promise<R>;

async function withValidating<R>(
  dispatch: AppDispatch,
  fn: () => Promise<R>
): Promise<R> {
  dispatch(incValidating());
  try {
    return await fn();
  } finally {
    dispatch(decValidating());
  }
}

/**
 * boot / full revalidation — validate_all_3D (kicked by configure-tunnel init).
 *
 * One request per tunnel, as legacy did — EXCEPT on a hybrid AHU, where `unit`
 * identifies the unit and the whole set comes back from a single
 * `ahu/units/<id>/validation?by_tunnel=1`. A hybrid unit routinely has 20-30
 * tunnels (box 7172 has 28), so that fan-out is 28 of the ~450 requests a
 * board load makes; the server returns the same per-tunnel payloads either
 * way. Standard AHUs keep the fan-out untouched — a handful of tunnels,
 * nothing to win, and no new code in their path.
 */
export const validateAll3D =
  (tunnelIds: number[], unit?: { ahuId: number; isHybrid?: boolean }): Thunk =>
  async (dispatch) => {
    await withValidating(dispatch, async () => {
      if (unit?.isHybrid) {
        const rows = await dispatch(
          ahuBoardApi.endpoints.getAhuUnitValidationByTunnel.initiate(unit.ahuId, {
            forceRefetch: true,
          })
        ).unwrap();
        // Emit in the caller's tunnel order, and only for the tunnels it asked
        // about, so the store ends up with exactly what the per-tunnel fan-out
        // would have put there: the unit response is server truth and may hold
        // a tunnel the board has not listed yet.
        const byId = new Map(rows.map((row) => [row.id, row]));
        dispatch(
          setAllErrors(
            tunnelIds
              .map((tunnelId) => byId.get(tunnelId))
              .filter((row): row is ITunnelValidation => row != null)
          )
        );
        return;
      }
      const results = await Promise.all(
        tunnelIds.map((tunnelId) =>
          dispatch(
            ahuBoardApi.endpoints.getTunnelValidation.initiate(
              { tunnelId },
              { forceRefetch: true }
            )
          ).unwrap()
        )
      );
      dispatch(setAllErrors(results));
    }).catch(() => undefined);
  };

/** validateTunnel(id) — await-able, returns the response (resolution pipeline). */
export const validateTunnel =
  (tunnelId: number): Thunk<ITunnelValidation | null> =>
  async (dispatch) =>
    withValidating(dispatch, async () => {
      const res = await dispatch(
        ahuBoardApi.endpoints.getTunnelValidation.initiate(
          { tunnelId },
          { forceRefetch: true }
        )
      ).unwrap();
      dispatch(replaceTunnel(res));
      return res;
    }).catch(() => null);

/** validateTunnelOnlyComponents({tunnelId|sectionId}). */
export const validateTunnelOnlyComponents =
  (arg: { tunnelId?: number; sectionId?: number }): Thunk =>
  async (dispatch, getState) => {
    let { tunnelId } = arg;
    if (tunnelId == null && arg.sectionId != null) {
      // sectionId→tunnel resolution by scanning current errors (unit.js).
      const errors = getState().ahuValidation.errors;
      for (const err of errors) {
        if (
          err.validation_error_messages?.sections?.some((s) => s.id === arg.sectionId)
        ) {
          tunnelId = err.id;
          break;
        }
      }
      if (tunnelId == null) {
        // Fallback: scan section caches.
        const state = getState();
        const queries = (state[ahuBoardApi.reducerPath] as {
          queries: Record<string, { endpointName?: string; data?: unknown } | undefined>;
        }).queries;
        Object.values(queries).forEach((q) => {
          if (q?.endpointName === 'getTunnelSectionList' && Array.isArray(q.data)) {
            const rows = q.data as Array<{ id: number; tunnel_id: number }>;
            const hit = rows.find((r) => r.id === arg.sectionId);
            if (hit) tunnelId = hit.tunnel_id;
          }
        });
      }
    }
    if (tunnelId == null) return;
    const finalTunnelId = tunnelId;
    await withValidating(dispatch, async () => {
      const res = await dispatch(
        ahuBoardApi.endpoints.getTunnelValidation.initiate(
          { tunnelId: finalTunnelId, componentsOnly: true },
          { forceRefetch: true }
        )
      ).unwrap();
      dispatch(mergeTunnelComponents(res));
    }).catch(() => undefined);
  };

/** validateOnNewComponent / validateOnSectionChange (same merge). */
export const validateOnNewComponent =
  (componentId: number): Thunk =>
  async (dispatch) => {
    await withValidating(dispatch, async () => {
      const res = await dispatch(
        ahuBoardFormsApi.endpoints.getComponentValidation.initiate(componentId, {
          forceRefetch: true,
        })
      ).unwrap();
      dispatch(
        mergeComponentValidation({
          sectionId: Number(res.section_id),
          tunnelId: Number(res.tunnel_id),
          sectionLabel: String(res.section ?? ''),
          items: res.validation_error_messages ?? [],
        })
      );
    }).catch(() => undefined);
  };

/** validateOnNewAccessory — concat; res.selected → section sweep follow-up. */
export const validateOnNewAccessory =
  (accessoryId: number): Thunk =>
  async (dispatch) => {
    await withValidating(dispatch, async () => {
      const res = await dispatch(
        ahuBoardFormsApi.endpoints.getAccessoryValidation.initiate(accessoryId, {
          forceRefetch: true,
        })
      ).unwrap();
      dispatch(
        appendAccessoryValidation({
          sectionId: Number(res.section_id),
          tunnelId: Number(res.tunnel_id),
          sectionLabel: String(res.section ?? ''),
          items: res.validation_error_messages ?? [],
        })
      );
      if (res.selected) {
        await dispatch(validateSectionAccessories(Number(res.section_id)));
      }
    }).catch(() => undefined);
  };

/** validateAccessoryOnChange — array-normalized; stale removal + concat. */
export const validateAccessoryOnChange =
  (accessoryId: number): Thunk =>
  async (dispatch) => {
    await withValidating(dispatch, async () => {
      const rows = await dispatch(
        ahuBoardFormsApi.endpoints.getAccessoriesValidation.initiate(accessoryId, {
          forceRefetch: true,
        })
      ).unwrap();
      dispatch(
        mergeAccessoryOnChange(
          rows.map((row: IItemValidation) => ({
            sectionId: Number(row.section_id),
            accessoryId: Number(row.id),
            items: row.validation_error_messages ?? [],
          }))
        )
      );
    }).catch(() => undefined);
  };

/** validateSectionAccessories / validateOnAccessoryDestroy — wholesale replace. */
export const validateSectionAccessories =
  (sectionId: number): Thunk =>
  async (dispatch) => {
    await withValidating(dispatch, async () => {
      const rows = await dispatch(
        ahuBoardFormsApi.endpoints.getSectionAccessoriesValidation.initiate(sectionId, {
          forceRefetch: true,
        })
      ).unwrap();
      const items = rows.flatMap((row) => row.validation_error_messages ?? []);
      dispatch(replaceSectionAccessories({ sectionId, items }));
    }).catch(() => undefined);
  };

/** validateOnSplitDmountChange — stamps the dmount id onto the first item. */
export const validateOnSplitDmountChange =
  (dmountId: number): Thunk =>
  async (dispatch) => {
    await withValidating(dispatch, async () => {
      const res = await dispatch(
        ahuBoardFormsApi.endpoints.getDmountValidation.initiate(dmountId, {
          forceRefetch: true,
        })
      ).unwrap();
      const items = (res.validation_error_messages ?? []).map((item, i) =>
        i === 0 ? { ...item, id: dmountId } : item
      );
      dispatch(
        mergeDmountValidation({
          sectionId: Number(res.section_id),
          dmountId,
          items,
        })
      );
    }).catch(() => undefined);
  };

export const validateOnSplitDmountDestroy =
  (sectionId: number, dmountId: number): Thunk =>
  async (dispatch) => {
    dispatch(removeDmountErrors({ sectionId, dmountId }));
  };

/** validateOnSectionDestroy(sectionId, skip, tunnelId, tunnel_removed). */
export const validateOnSectionDestroy =
  (arg: {
    sectionId: number;
    skip?: boolean;
    tunnelId?: number;
    tunnel_removed?: boolean;
  }): Thunk =>
  async (dispatch) => {
    dispatch(
      clearSectionOnDestroy({
        sectionId: arg.sectionId,
        tunnelRemoved: arg.tunnel_removed,
        tunnelId: arg.tunnelId,
      })
    );
    if (!arg.skip) {
      await dispatch(
        validateTunnelOnlyComponents(
          arg.tunnelId != null ? { tunnelId: arg.tunnelId } : { sectionId: arg.sectionId }
        )
      );
    }
  };

export const removeErrorsAfterTunnelDeleting =
  (tunnelId: number): Thunk =>
  async (dispatch) => {
    dispatch(removeTunnelErrors(tunnelId));
  };

/**
 * section.checkChangesAndValidate (§22.4 / section.js): routes a section save's
 * changed fields to the right partial validator. Caller supplies position
 * info (first/last in tunnel) + the section's first component id.
 */
const SECTION_VALIDATED_FIELDS = [
  'length',
  'casing_outer_wall_mtl_gauge',
  'outerliner_id',
  'casing_insulation_id',
  'casing_innerliner_id',
  'floor_innerliner_id',
  'auto_adjust',
];

export const checkSectionChangesAndValidate =
  (arg: {
    sectionId: number;
    tunnelId: number;
    sectionLabel: string;
    changes: Record<string, unknown>;
    isFirstOrLast: boolean;
    firstComponentId: number | null;
  }): Thunk =>
  async (dispatch) => {
    const keys = Object.keys(arg.changes);
    if (keys.includes('tag') || keys.includes('label')) {
      dispatch(
        updateSectionTag({ sectionId: arg.sectionId, sectionLabel: arg.sectionLabel })
      );
    }
    const validated = keys.filter((k) => SECTION_VALIDATED_FIELDS.includes(k));
    if (!validated.length) return;
    if (validated.includes('length') || validated.includes('auto_adjust')) {
      await dispatch(validateTunnelOnlyComponents({ tunnelId: arg.tunnelId }));
      await dispatch(validateSectionAccessories(arg.sectionId));
      return;
    }
    const nonFloor = validated.some(
      (k) => k !== 'floor_innerliner_id' && k !== 'casing_outer_wall_mtl_gauge'
    );
    if (nonFloor && arg.isFirstOrLast) {
      await dispatch(validateTunnel(arg.tunnelId));
      return;
    }
    if (arg.firstComponentId != null) {
      await dispatch(validateOnNewComponent(arg.firstComponentId));
    }
    await dispatch(validateSectionAccessories(arg.sectionId));
  };
